import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, Loader2, Upload, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';

// Phase 2B — governed Excel mass import of GLOBAL Role Profiles (hm_admin).
// Validation preview first, explicit commit second — no silent partial import.
export const RoleProfileImportDialog = ({ open, onOpenChange, onImported }) => {
  const { t, i18n } = useTranslation();
  const isES = (i18n.language || 'es').startsWith('es');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setPreview(null); setResult(null); };

  const downloadTemplate = async () => {
    try {
      const res = await api.get('/admin/role_profiles/import/template', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'CTH_role_profiles_import_template.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runPreview = async (f) => {
    setBusy(true); setPreview(null); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const { data } = await api.post('/admin/role_profiles/import/preview', fd);
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e)); setFile(null); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const validRows = preview.rows.filter((r) => r.valid);
      const { data } = await api.post('/admin/role_profiles/import/confirm', {
        filename: preview.filename, rows: validRows,
      });
      setResult(data);
      toast.success(isES
        ? `${data.imported} perfiles de puesto importados como Plantillas Globales CTH`
        : `${data.imported} role profiles imported as Global CTH Templates`);
      onImported?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const errLabel = (er) => {
    const map = {
      required_field: isES ? 'Campo obligatorio' : 'Required field',
      invalid_code_format: isES ? 'Formato de código inválido' : 'Invalid code format',
      duplicate_in_file: isES ? 'Código duplicado en el archivo' : 'Duplicate code in file',
      code_already_exists: isES ? 'El código ya existe en el catálogo' : 'Code already exists',
      unknown_competency_codes: isES ? 'Códigos de competencia inexistentes' : 'Unknown competency codes',
      invalid_level: isES ? 'Nivel inválido (0-100)' : 'Invalid level (0-100)',
      invalid_criticality: isES ? 'Criticidad inválida' : 'Invalid criticality',
    };
    const base = `${er.column}: ${map[er.error] || er.error}`;
    return er.codes ? `${base} (${er.codes.join(', ')})` : er.value ? `${base} — ${er.value}` : base;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="rp-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-primary" />
            {isES ? 'Importar Perfiles de Puesto (Excel)' : 'Import Role Profiles (Excel)'}
          </DialogTitle>
          <DialogDescription>
            {isES
              ? 'Importación gobernada de Plantillas Globales CTH. Las competencias deben referenciar códigos existentes del catálogo — nunca se crean automáticamente.'
              : 'Governed import of Global CTH Templates. Competencies must reference existing catalog codes — never auto-created.'}
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="rp-import-template-btn">
                <Download className="h-4 w-4 mr-1.5" /> {isES ? 'Descargar plantilla' : 'Download template'}
              </Button>
              <label className="inline-flex">
                <input type="file" accept=".xlsx" className="hidden" data-testid="rp-import-file-input"
                       onChange={(e) => { const f = e.target.files?.[0]; if (f) { setFile(f); runPreview(f); } e.target.value = ''; }} />
                <span className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border text-sm font-medium cursor-pointer hover:bg-muted/50">
                  <Upload className="h-4 w-4" /> {isES ? 'Seleccionar archivo .xlsx' : 'Choose .xlsx file'}
                </span>
              </label>
              {file && <span className="text-xs text-muted-foreground">{file.name}</span>}
              {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            </div>

            {preview && (
              <div className="space-y-3" data-testid="rp-import-preview">
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="tabular-nums" data-testid="rp-import-total">
                    {isES ? 'Filas' : 'Rows'}: {preview.total}
                  </Badge>
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 tabular-nums" data-testid="rp-import-valid">
                    {isES ? 'Válidas' : 'Valid'}: {preview.valid}
                  </Badge>
                  <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-200 tabular-nums" data-testid="rp-import-invalid">
                    {isES ? 'Con errores' : 'With errors'}: {preview.invalid}
                  </Badge>
                  {preview.warnings > 0 && (
                    <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 tabular-nums">
                      {isES ? 'Avisos' : 'Warnings'}: {preview.warnings}
                    </Badge>
                  )}
                </div>
                <div className="border border-border rounded-md divide-y max-h-72 overflow-y-auto">
                  {preview.rows.map((r) => (
                    <div key={r.row} className="p-2.5 text-sm flex items-start gap-2" data-testid={`rp-import-row-${r.row}`}>
                      {r.valid
                        ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                        : <XCircle className="h-4 w-4 text-rose-600 mt-0.5 shrink-0" />}
                      <div className="min-w-0">
                        <p className="font-medium">
                          {isES ? 'Fila' : 'Row'} {r.row} — {r.data.position_title || '—'}
                          <span className="text-xs font-mono text-muted-foreground ml-2">{r.data.code}</span>
                          <span className="text-xs text-muted-foreground ml-2">
                            {r.requirements.length} {isES ? 'competencias' : 'competencies'}
                          </span>
                        </p>
                        {r.errors.map((er, i) => (
                          <p key={i} className="text-xs text-rose-700">{errLabel(er)}</p>
                        ))}
                        {r.warnings.map((w, i) => (
                          <p key={i} className="text-xs text-amber-700 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" /> {w.column}: {w.warning}
                          </p>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {preview.invalid > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {isES
                      ? 'Las filas con errores NO se importarán. Puede corregir el archivo y volver a cargarlo, o importar explícitamente solo las filas válidas.'
                      : 'Rows with errors will NOT be imported. Fix the file and re-upload, or explicitly import only the valid rows.'}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="p-4 rounded-md bg-emerald-50 border border-emerald-200 text-sm" data-testid="rp-import-result">
            <p className="font-semibold text-emerald-800">
              {isES ? 'Importación completada' : 'Import complete'}
            </p>
            <p className="text-emerald-800">
              {result.imported} {isES ? 'perfiles creados como Plantillas Globales CTH (auditado).' : 'profiles created as Global CTH Templates (audited).'}
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t('common.close', { defaultValue: isES ? 'Cerrar' : 'Close' })}</Button>
          {!result && (
            <Button onClick={commit} disabled={busy || !preview || preview.valid === 0} data-testid="rp-import-commit-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (isES
                ? `Importar ${preview?.valid ?? 0} filas válidas`
                : `Import ${preview?.valid ?? 0} valid rows`)}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
